import {
    Component,
    OnInit,
    HostListener,
    Input,
    OnChanges,
    DestroyRef,
    inject,
    EventEmitter,
    Output, SimpleChanges
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
// tslint:disable-next-line:max-line-length
import { EditConsumerAttributesDialogComponent } from '@emm/shared/dialogs/edit-consumer-attributes-dialog/edit-consumer-attributes-dialog.component';
import {Observable, combineLatestWith, BehaviorSubject} from 'rxjs';
import { FormControl, FormGroup } from '@angular/forms';
import { TreeNode } from '@ali-hm/angular-tree-component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AuthFacadeService} from '@emm/auth/services/auth-facade.service';
import {ManagementApiService} from '@emm/management/services/management-api.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {Consumer} from '@emm/core/models/consumer.model';
import { Attribute } from '@emm/core/models/attribute.model';
import {SalesChannel} from '@emm/core/models/sales-channel.model';
import {API_ERRORS} from '@emm/core/util/api-constants';
import {take} from 'rxjs/operators';

@Component({
    selector: 'emm-consumer-manager-form',
    styleUrls: ['./consumer-manager-form.component.scss'],
    templateUrl: './consumer-manager-form.component.html'
})
export class ConsumerManagerFormComponent implements OnInit, OnChanges {

    private readonly destroyRef: DestroyRef = inject(DestroyRef);

    // tslint:disable-next-line:variable-name
    _selectedConsumerInfo: any;
    @Input() set selectedConsumerInfo(value: any) {
        this._selectedConsumerInfo = value;
        this.getData();
    }
    @Input() node: TreeNode;
    @Input() isSuperAdmin: boolean;
    @Input() isAdmin: boolean;
    @Output() toggleButtons = new EventEmitter<void>();
    canEdit: boolean;

    isReadOnly$ = this.authFacade.isReadOnly$;
    attributes$ = this.managementApiService.getAllGlobalAttributes();
    attributeExclusions$: Observable<string[]>;
    assignedAttributes: Attribute[];
    unassignedAttributes: Attribute[];
    initialAssignedAttributes: Attribute[];
    initialUnassignedAttributes: Attribute[];
    changesMade = false;
    form: FormGroup;
    consumerTypes$ = this.managementApiService.getConsumerTypes();
    salesChannels: SalesChannel[] = [];

    constructor(
        private authFacade: AuthFacadeService,
        private managementApiService: ManagementApiService,
        private dialog: MatDialog,
        private notificationService: NotificationService
    ) {}

    ngOnInit(): void {
        this.managementApiService.getAllSalesChannels().pipe(take(1)).subscribe(
            {
                next: (result) => {
                    this.salesChannels = result
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            });
        this.canEdit = this.isSuperAdmin || this.isAdmin;
        this.buildForm();
    }

    ngOnChanges(changes: SimpleChanges): void {
        this.getData();
        this.buildForm();
        console.log(changes);
    }

    buildForm(): void {
        this.form = new FormGroup({
            destinationType: new FormControl(this._selectedConsumerInfo.consumerTypeTag),
            description: new FormControl(this._selectedConsumerInfo.description),
            salesChannels: new FormControl({value: this._selectedConsumerInfo.salesChannelList, disabled: false}),
        });
        if (!this.canEdit) {
            this.form.disable();
            // this.toggleFormButtons();
        }
    }

    getData(): void {
        this.attributeExclusions$ = this.managementApiService.getAttributeExclusionsByConsumer(this._selectedConsumerInfo['tag']);

        // tslint:disable-next-line:max-line-length
        this.attributes$
            .pipe(
                combineLatestWith(this.attributeExclusions$),
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe({
                next: ([attributes, exclusions]) => {

                        this.assignedAttributes = attributes.filter(a => !exclusions.includes(a.tag));
                        this.unassignedAttributes = attributes.filter(a => exclusions.includes(a.tag));

                        this.initialAssignedAttributes = [].concat(this.assignedAttributes);
                        this.initialUnassignedAttributes = [].concat(this.unassignedAttributes);
                },
                error: (error) => {
                    this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                }
            });
    }

    cancel(): void {
        if (this.changesMade) {
            this.assignedAttributes = [].concat(this.initialAssignedAttributes);
            this.unassignedAttributes = [].concat(this.initialUnassignedAttributes);
        }
        this.changesMade = false;
        this.notificationService.error('Cancelled Edit Consumer Attributes');
        const initialValues = {
            destinationType: this._selectedConsumerInfo.consumerTypeTag,
            description: this._selectedConsumerInfo.description,
            salesChannels: this._selectedConsumerInfo.salesChannelList,
        }
        this.form.reset(initialValues);
        this.form.markAsPristine();
    }

    submit(): void {
        if (this.hasChanges() && this.isFormReady()) {
            const updatedConsumer = {
                description: this.form?.controls?.description.value,
                destinationType: this.form?.controls?.destinationType.value,
                deleted: this._selectedConsumerInfo.deleted,
                enabled: this._selectedConsumerInfo.enabled,
                locked: this._selectedConsumerInfo.locked,
                name: this._selectedConsumerInfo.name,
                tag: this._selectedConsumerInfo.tag,
                consumerCategory: this._selectedConsumerInfo.consumerCategory,
                salesChannelList: this.form?.controls?.salesChannels.value
            } as Consumer;
            // This endpoint has not been implemented on backend
            this.managementApiService.updateConsumer(updatedConsumer.tag, updatedConsumer)
                .pipe(
                    takeUntilDestroyed(this.destroyRef)
                )
                .subscribe(
                    {
                        next: (res) => {
                            this.notificationService.success('Success - Consumer Updates Saved.');
                            this.form.markAsPristine();
                        },
                        error: (error) => {
                            this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                        }
                    });
        }
    }

    editAssignedAttributes(): void {
        this.openEditConsumerAttributesDialog();
    }

    hasChanges(): boolean {
        return this.form && this.form.dirty;
    }

    isFormReady(): boolean {
        return this.form?.dirty && !this.form?.invalid;
    }

    // TODO: Remove this flow for toggle buttons after Menu-4629 passes testing
    toggleFormButtons() {
        this.toggleButtons.next();
    }

    openEditConsumerAttributesDialog(): void {
        const dialogRef = this.dialog.open(EditConsumerAttributesDialogComponent, {
            width: '600px',
            data: {
                assignedAttributes: this.assignedAttributes,
                unassignedAttributes: this.unassignedAttributes
            }
        });
        dialogRef
            .afterClosed()
            .pipe(
                takeUntilDestroyed(this.destroyRef)
            )
            .subscribe(editedAttributes => {
                if (editedAttributes !== undefined) {

                    this.assignedAttributes = editedAttributes['assignedAttributes'];
                    this.unassignedAttributes = editedAttributes['unassignedAttributes'];

                    this.changesMade = editedAttributes['changesMade'];

                    const excludeTags = this.unassignedAttributes.map(ua => ua.tag);

                    // tslint:disable-next-line:max-line-length
                    this.managementApiService.updateAttributeExclusionsByConsumer(this._selectedConsumerInfo['tag'], excludeTags)
                        .pipe(
                            takeUntilDestroyed(this.destroyRef)
                        )
                        .subscribe({
                            next: (ae) => {
                                this.changesMade = false;
                                this.form.markAsPristine();
                                this.notificationService.success('Success - Attribute Updates Saved.');
                            },
                            error: (error) => {
                                this.form.markAsPristine();
                                this.notificationService.error(error?.error?.error?.message || API_ERRORS.GenericErrorMessage);
                            }
                        });

                } else {
                    // User clicked cancel
                    this.changesMade = false;
                    this.form.markAsPristine();
                    this.notificationService.error('Cancelled Edit Consumer Attributes');
                }
            });
    }

}
